import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { UserRoleEnum } from '@enum/role.enum';

import { UserPermissionService } from '@services/helper/user-permission.service';

/** The one role these forms are closed to — read-only by definition. */
const READ_ONLY_ROLES: readonly string[] = [UserRoleEnum.DeveloperViewer];

/**
 * FEC-1225 (Hina, 17 Sep): "Remove add and edit rights" for the read-only
 * Developer Viewer.
 *
 * Guards the add / edit sub-pages under a project — Upgrade Type, Upgrade
 * Details, Variation Type, Colour Scheme Type. The list pages hide the buttons
 * that lead here, but these are plain URLs: without a guard a viewer could
 * still type one in and be handed a form whose Save only ever 403s.
 *
 * Reads the PERSISTED role, as `dashboardAccessGuard` does — guards run before
 * the header has fetched `/me`, so on a cold load the in-memory user is empty.
 * A denied visitor lands on the list page the form belongs to.
 */
export const projectContentWriteGuard: CanActivateFn = (route) => {
  const userPermission = inject(UserPermissionService);
  const router = inject(Router);

  /* A deny-list on purpose: the ticket is about the Developer Viewer and
     nothing else, so every other role passes exactly as it did before this
     guard existed (including a cold load whose role is not persisted yet). */
  const role = userPermission.getPersistedRoleName() ?? '';
  if (!READ_ONLY_ROLES.includes(role)) return true;

  /* Legacy `/project/:projectId/upgrade…` routes name the param differently and
     both of them belong to the Upgrades list. */
  const legacyProjectId = route.paramMap.get('projectId');
  if (legacyProjectId) {
    return router.createUrlTree([`/project-detail/${legacyProjectId}/upgrades`]);
  }

  const projectId = route.parent?.paramMap.get('id') ?? route.paramMap.get('id');
  /* `upgrades/types/add` → `upgrades`, `color-schemes/types/add` → `color-schemes`, … */
  const listSegment = route.routeConfig?.path?.split('/')[0] || 'view';
  return router.createUrlTree([`/project-detail/${projectId}/${listSegment}`]);
};
